import {
  AccessDeniedError,
  BadRequestError,
  ConnectionError,
  HttpTypes,
  InternalServerError,
  TooManyRequestsError,
  UnexpectedError,
} from 'src/http';
import { HttpError } from 'src/http/errors/http-error';
import { httpResponseHandler } from 'src/http/http-response-handler';
import { Benefit } from 'src/types/benefits/benefit';
import { HttpDto } from 'src/types/common/http-dto';
import { Either } from 'src/utils/either';

export class BenefitsRepository {
  constructor(private api: HttpTypes.HttpClient) {}

  async createBenefit(body: any, csvFile?: File): Promise<Either<HttpError, any | undefined>> {
    const formData = new FormData();
    Object.keys(body).forEach((key) => {
      if (body[key] !== undefined && body[key] !== null && body[key] !== '') {
        if (Array.isArray(body[key])) {
          if (key === 'howToUse') {
            formData.append(key, JSON.stringify(body[key]));
          } else {
            body[key].forEach((item: any) => {
              formData.append(`${key}[]`, item);
            });
          }
        } else if (typeof body[key] === 'object' && !(body[key] instanceof File) && !(body[key] instanceof Blob)) {
          formData.append(key, JSON.stringify(body[key]));
        } else {
          formData.append(key, body[key]);
        }
      }
    });
    if (csvFile && body.voucherType === 'import') {
      formData.append('file', csvFile);
    }

    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `benefits`,
      body: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getBenefits(
    params?: any
  ): Promise<
    Either<
      BadRequestError | UnexpectedError | InternalServerError | AccessDeniedError | ConnectionError | TooManyRequestsError,
      any | undefined | HttpDto.PaginationResponse<Benefit>
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits`,
      params,
    };

    const response = await this.api.request(request);
    return httpResponseHandler<HttpDto.PaginationResponse<Benefit>>(response);
  }

  async getPromotionOptions(
    params?: any
  ): Promise<
    Either<
      BadRequestError | UnexpectedError | InternalServerError | AccessDeniedError | ConnectionError | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `tickets/parking-promotions`,
      params,
    };

    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }

  async changeStatus(
    id: string
  ): Promise<
    Either<
      BadRequestError | UnexpectedError | InternalServerError | AccessDeniedError | ConnectionError | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: `benefits/change-status/${id}`,
    };

    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }

  async uploadImage(body?: any): Promise<Either<HttpError, any | undefined>> {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `media/upload/base64/benefits`,
      body,
    };
    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }

  async updateBenefit(id: string, body: any, csvFile?: File): Promise<Either<HttpError, any | undefined>> {
    const formData = new FormData();

    Object.keys(body).forEach((key) => {
      if (body[key] !== undefined && body[key] !== null && body[key] !== '') {
        if (Array.isArray(body[key])) {
          if (key === 'howToUse') {
            formData.append(key, JSON.stringify(body[key]));
          } else {
            body[key].forEach((item: any) => {
              formData.append(`${key}[]`, item);
            });
          }
        } else if (typeof body[key] === 'object' && !(body[key] instanceof File) && !(body[key] instanceof Blob)) {
          formData.append(key, JSON.stringify(body[key]));
        } else {
          formData.append(key, body[key]);
        }
      }
    });

    if (csvFile && body.voucherType === 'import') {
      formData.append('file', csvFile);
    }
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: `benefits/${id}`,
      body: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }

  async deleteBenefit(id: string): Promise<Either<HttpError, any | undefined>> {
    const request: HttpTypes.HttpRequest = {
      method: 'delete',
      url: `benefits/${id}`,
    };
    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }

  async getStores(
    params?: any
  ): Promise<
    Either<
      BadRequestError | UnexpectedError | InternalServerError | AccessDeniedError | ConnectionError | TooManyRequestsError,
      any | undefined | HttpDto.PaginationResponse<any>
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `stores/all-stores-admin`,
      params: {
        limit: 100,
        status: 'active',
        ...params,
      },
    };
    const response = await this.api.request(request);
    return httpResponseHandler<HttpDto.PaginationResponse<any>>(response);
  }

  async getRestaurants(
    params?: any
  ): Promise<
    Either<
      BadRequestError | UnexpectedError | InternalServerError | AccessDeniedError | ConnectionError | TooManyRequestsError,
      any | undefined | HttpDto.PaginationResponse<any>
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `gastronomy/all-restaurants-admin`,
      params: {
        limit: 100,
        status: 'active',
        ...params,
      },
    };
    const response = await this.api.request(request);
    return httpResponseHandler<HttpDto.PaginationResponse<any>>(response);
  }

  async getCategories(params?: any): Promise<Either<HttpError, any>> {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefit-config/get-all`,
      params: {
        limit: 100,
        status: 'active',
        ...params,
      },
    };
    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }

  async getLevels(
    params?: any
  ): Promise<
    Either<
      BadRequestError | UnexpectedError | InternalServerError | AccessDeniedError | ConnectionError | TooManyRequestsError,
      any | undefined | HttpDto.PaginationResponse<any>
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `levels`,
      params,
    };
    const response = await this.api.request(request);
    return httpResponseHandler<HttpDto.PaginationResponse<any>>(response);
  }

  async getRedeemedVouchersByBenefitId(id: string): Promise<Either<HttpError, any | undefined>> {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/redeemed-vouchers/${id}`,
    };
    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }

  async getVoucherStats(id: string): Promise<Either<HttpError, any>> {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/voucher-stats/${id}`,
    };
    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }

  async getVouchersByBenefitId(id: string): Promise<Either<HttpError, any | undefined>> {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/vouchers/${id}`,
    };
    const response = await this.api.request(request);
    return httpResponseHandler(response);
  }
}
